// ============================================================================
// CANCUN100 — CrearContrasena.js  (Bloque 19: pantalla /crear-contrasena)
// ----------------------------------------------------------------------------
// Llega aquí el agente desde el correo de invitación (o de "Olvidé mi contraseña").
// Supabase deja la sesión del link en la URL; supabase-js la toma sola (detectSessionInUrl).
// Reglas:
//  · La invitación DEBE traer user_metadata.agente_id (agente) o admin_id (administrador, Bloque 21). Si no:
//    "Invitación inválida, pide otra" y no se crea nada.
//  · Contraseña + confirmar, con ojito 👁 para ver/ocultar; mínimo 8 con letras y números; botón apagado
//    hasta que coincidan.
//  · Al guardar: auth.updateUser({ password }) y, SOLO para ese agente_id,
//    agentes.login_pin_activo=false, contrasena_creada_en=now(), auth_uid=user.id.
//    Sesión, folios, comisiones e historial no se tocan (mismo agentes.id).
//  · Link viejo de invitación con contraseña ya creada: "Ya tienes contraseña, entra con tu correo".
// ============================================================================
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { RADIO } from './tema';
import CampoSecreto from './CampoSecreto'; // Hotfix 15-oct: un solo ojito para toda la app

export const RUTA_CREAR_CONTRASENA = '/crear-contrasena';

// Tipo de link (invite | recovery | signup…) y error, leídos ANTES de que supabase-js limpie la URL.
const LINK = (() => {
  try {
    const h = new URLSearchParams(String(window.location.hash || '').replace(/^#/, ''));
    const qy = new URLSearchParams(window.location.search || '');
    return {
      tipo: h.get('type') || qy.get('type') || null,
      error: h.get('error_description') || qy.get('error_description') || h.get('error') || qy.get('error') || null,
    };
  } catch { return { tipo: null, error: null }; }
})();

export const reglasContrasena = (p) => {
  const s = String(p || '');
  return { largo: s.length >= 8, letras: /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(s), numeros: /\d/.test(s) };
};
const valida = (p) => { const r = reglasContrasena(p); return r.largo && r.letras && r.numeros; };


// Campo de contraseña con ojito (a nivel de módulo para no perder el foco al teclear).
const CampoContrasena = ({ C, etiqueta, valor, onChange, autoFocus }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{etiqueta}</div>
    <CampoSecreto C={C} value={valor} onChange={onChange} autoFocus={autoFocus} autoComplete="new-password" style={{ background: C.surface, fontSize: 14 }} />
  </div>
);

// Marco de la pantalla (a nivel de módulo: si viviera en el render, los campos perderían el foco).
const Marco = ({ C, children }) => (
  <div style={{ minHeight: '100vh', background: `linear-gradient(135deg,${C.dark},${C.bg2 || C.dark},${C.dark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--au-font-text)', padding: 20 }}>
    <div style={{ width: '100%', maxWidth: 420 }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <div style={{ background: `${C.grad}`, borderRadius: RADIO, width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22, color: '#fff', fontFamily: 'var(--au-font-text)' }}>C1</div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 800, fontSize: 22, color: C.text, fontFamily: 'var(--au-font-text)' }}>CANCUN<span style={{ color: C.rosa }}>100</span></div>
            <div style={{ fontSize: 12, color: C.muted, letterSpacing: 3, textTransform: 'uppercase' }}>Centro de Control</div>
          </div>
        </div>
      </div>
      <div className="au-glass" style={{ padding: 22, background: C.card }}>{children}</div>
    </div>
  </div>
);

export default function CrearContrasena({ C, onListo, onIrALogin }) {
  const [estado, setEstado] = useState(LINK.error ? 'vencido' : 'cargando'); // cargando | form | invalido | vencido | ya | guardando
  const [usuario, setUsuario] = useState(null);
  const [agente, setAgente] = useState(null);
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [error, setError] = useState('');
  const esRecuperacion = LINK.tipo === 'recovery';

  useEffect(() => {
    if (LINK.error) return undefined;
    let vivo = true;
    let resuelto = false;
    const revisar = async (sesion) => {
      if (!vivo || resuelto || !sesion?.user) return;
      resuelto = true;
      const u = sesion.user;
      // Bloque 21: la invitación puede ser de un agente (agente_id) o de un administrador (admin_id).
      const adminId = Number(u.user_metadata?.admin_id);
      const agenteId = Number(u.user_metadata?.agente_id);
      const esAdmin = Number.isInteger(adminId) && adminId > 0;
      const idPersona = esAdmin ? adminId : agenteId;
      if (!Number.isInteger(idPersona) || idPersona <= 0) { setEstado('invalido'); return; }
      const { data: ag, error: e } = await supabase.from(esAdmin ? 'admins' : 'agentes').select('id,nombre,activo,auth_uid,contrasena_creada_en').eq('id', idPersona).maybeSingle();
      if (!vivo) return;
      if (e || !ag || (ag.auth_uid && ag.auth_uid !== u.id)) { setEstado('invalido'); return; }
      if (ag.activo === false) { setEstado('invalido'); setError('Tu acceso está desactivado. Contacta a tu supervisor.'); return; }
      setUsuario(u); setAgente({ ...ag, tipo: esAdmin ? 'admin' : 'agente' });
      setEstado(ag.contrasena_creada_en && !esRecuperacion ? 'ya' : 'form');
    };
    supabase.auth.getSession().then(({ data }) => revisar(data?.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, sesion) => revisar(sesion));
    // Sin sesión en unos segundos = link inválido o ya usado.
    const t = setTimeout(() => { if (vivo && !resuelto) setEstado('invalido'); }, 6000);
    return () => { vivo = false; clearTimeout(t); sub?.subscription?.unsubscribe(); };
  }, [esRecuperacion]);

  const r = reglasContrasena(p1);
  const coinciden = p1 !== '' && p1 === p2;
  const listo = valida(p1) && coinciden && estado === 'form';

  const guardar = async () => {
    if (!listo || !usuario || !agente) return;
    setEstado('guardando'); setError('');
    const { error: e1 } = await supabase.auth.updateUser({ password: p1 });
    if (e1) { setEstado('form'); setError(`No se pudo guardar la contraseña: ${e1.message}`); return; }
    // SOLO ese agente_id; y solo si no está ligado a otra cuenta de Auth.
    const esAdmin = agente.tipo === 'admin';
    const cambios = esAdmin ? { auth_uid: usuario.id } : { auth_uid: usuario.id, login_pin_activo: false, actualizado_por: agente.nombre || 'agente' };
    if (!agente.contrasena_creada_en) cambios.contrasena_creada_en = new Date().toISOString();
    const { data: filas, error: e2 } = await supabase.from(esAdmin ? 'admins' : 'agentes').update(cambios)
      .eq('id', agente.id).or(`auth_uid.is.null,auth_uid.eq.${usuario.id}`).select('id');
    if (e2 || !filas || !filas.length) {
      setEstado('form');
      setError(e2 ? `Tu contraseña quedó guardada, pero no se pudo ligar tu cuenta: ${e2.message}` : 'Esta invitación no corresponde a tu cuenta. Pide otra.');
      return;
    }
    await onListo({ tipo: agente.tipo, id: agente.id });
  };

  const botonLogin = (
    <button type="button" onClick={onIrALogin} style={{ width: '100%', marginTop: 16, background: `${C.grad}`, color: '#fff', border: 'none', borderRadius: RADIO, padding: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--au-font-text)' }}>Ir a iniciar sesión</button>
  );

  if (estado === 'cargando') return <Marco C={C}><div style={{ color: C.muted, textAlign: 'center', padding: 20 }}>Revisando tu invitación…</div></Marco>;
  if (estado === 'vencido') return <Marco C={C}><div style={{ color: C.text, fontWeight: 700, marginBottom: 6 }}>El enlace ya venció o ya se usó</div><div style={{ color: C.muted, fontSize: 14 }}>Pide que te manden otra invitación, o usa "Olvidé mi contraseña" si ya la habías creado.</div>{botonLogin}</Marco>;
  if (estado === 'invalido') return <Marco C={C}><div style={{ color: C.text, fontWeight: 700, marginBottom: 6 }}>Invitación inválida, pide otra</div><div style={{ color: C.muted, fontSize: 14 }}>{error || 'No encontramos a quién corresponde este enlace. No se creó nada.'}</div>{botonLogin}</Marco>;
  if (estado === 'ya') return <Marco C={C}><div style={{ color: C.text, fontWeight: 700, marginBottom: 6 }}>Ya tienes contraseña, entra con tu correo</div><div style={{ color: C.muted, fontSize: 14 }}>Si no la recuerdas, usa "Olvidé mi contraseña" en la pantalla de inicio.</div>{botonLogin}</Marco>;

  const regla = (ok, txt) => <div style={{ fontSize: 12, color: ok ? C.green : C.muted }}>{ok ? '✓' : '○'} {txt}</div>;
  return (
    <Marco C={C}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>🔐</div>
        <div style={{ fontWeight: 700, color: C.text, fontSize: 16 }}>{esRecuperacion ? 'Nueva contraseña' : 'Crea tu contraseña'}</div>
        {agente?.nombre && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{agente.nombre} · {usuario?.email}</div>}
      </div>
      <CampoContrasena C={C} etiqueta="Contraseña" valor={p1} onChange={(v) => { setP1(v); setError(''); }} autoFocus />
      <CampoContrasena C={C} etiqueta="Confirmar contraseña" valor={p2} onChange={(v) => { setP2(v); setError(''); }} />
      <div style={{ marginBottom: 12 }}>
        {regla(r.largo, 'Mínimo 8 caracteres')}
        {regla(r.letras && r.numeros, 'Letras y números')}
        {regla(coinciden, 'Las dos coinciden')}
      </div>
      {error && <div style={{ color: C.rosa, fontSize: 12, marginBottom: 14, textAlign: 'center' }}>{error}</div>}
      <button type="button" onClick={guardar} disabled={!listo}
        style={{ width: '100%', background: `${C.grad}`, color: '#fff', border: 'none', borderRadius: RADIO, padding: 12, fontSize: 14, fontWeight: 700, cursor: listo ? 'pointer' : 'not-allowed', opacity: listo ? 1 : 0.45, fontFamily: 'var(--au-font-text)' }}>
        {estado === 'guardando' ? 'Guardando…' : 'Guardar y entrar'}
      </button>
    </Marco>
  );
}
